"use client";

import { useState, useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const testimonials = [
{
  id: "t-1",
  quote:
  "Guests are blown away when they tap my guitars and see the full provenance and signatures. It\'s like having a museum curator in my studio. Every piece now tells its complete story.",
  name: "Marcus Chen",
  role: "Vintage Guitar Collector",
  items: "187 Items Cataloged",
  avatar: "https://images.unsplash.com/photo-1638802766361-d105aab24268",
  avatarAlt: "Marcus Chen, music collector smiling in front of his vinyl collection",
  rating: 5
},
{
  id: "t-2",
  quote:
  "I run a sports memorabilia shop and TapTale has transformed how customers explore inventory. They can tap any jersey and see authentication, player stats, and game history instantly. Sales have increased measurably.",
  name: "David Kim",
  role: "Sports Collectibles Store Owner",
  items: "312 Items Cataloged",
  avatar: "https://images.unsplash.com/photo-1706062767760-4a7175fd3a74",
  avatarAlt: "David Kim, sports memorabilia dealer holding a signed baseball",
  rating: 5
},
{
  id: "t-3",
  quote:
  "The self-hosted option was the deciding factor for me. My collection represents decades of investment — having complete data sovereignty is non-negotiable. The Raspberry Pi setup was surprisingly painless.",
  name: "Sofia Andersson",
  role: "Fine Art Collector",
  items: "64 Items Cataloged",
  avatar: "https://images.unsplash.com/photo-1554747805-98a92d845661",
  avatarAlt: "Sofia Andersson, fine art collector in front of gallery wall",
  rating: 5
},
{
  id: "t-4",
  quote:
  "Our auction house uses TapTale for every lot. The interactive markers let bidders explore provenance details independently during preview days. We\'ve seen a measurable increase in buyer confidence and final hammer prices.",
  name: "James Okafor",
  role: "Auction House Director",
  items: "1,240 Items Cataloged",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a15fbf97-1763295285428.png",
  avatarAlt: "James Okafor, auction house director in formal attire",
  rating: 5
}];


export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 6000);
  };

  useEffect(() => {
    resetInterval();
    return () => {if (intervalRef.current) clearInterval(intervalRef.current);};
  }, []);

  const handleSelect = (i: number) => {
    setActive(i);
    resetInterval();
  };

  const current = testimonials[active];

  return (
    <section
      className="py-24 md:py-32 px-6 md:px-8 bg-bg-secondary relative overflow-hidden noise-overlay"
      aria-label="Testimonials">
      
      {/* Background blobs */}
      <div className="absolute top-0 left-1/3 w-96 h-96 rounded-full bg-accent-crimson/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-accent-purple/5 blur-[100px] pointer-events-none" />

      <div className="max-w-container mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-mono text-[11px] text-accent-crimson uppercase tracking-[0.3em] block mb-4">
            Community Voices
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight text-white">
            Collectors speak.
            <br />
            <span className="italic text-gray-400">Stories resonate.</span>
          </h2>
        </div>

        {/* Main Testimonial */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Large quote mark */}
            <span className="font-serif text-[10rem] italic absolute -top-12 -left-4 text-accent-crimson/10 leading-none pointer-events-none select-none">
              &ldquo;
            </span>

            <blockquote className="relative z-10">
              <p className="font-serif text-2xl md:text-3xl lg:text-4xl font-light italic leading-snug text-white mb-10">
                {current.quote}
              </p>

              <div className="flex items-center gap-6">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-accent-crimson/30 flex-shrink-0">
                  <AppImage
                    src={current.avatar}
                    alt={current.avatarAlt}
                    width={56}
                    height={56}
                    className="object-cover w-full h-full" />
                  
                </div>
                <div>
                  <div className="flex gap-0.5 mb-1.5">
                    {Array.from({ length: current.rating }).map((_, i) =>
                    <Icon key={i} name="StarIcon" size={14} className="text-accent-gold" variant="solid" />
                    )}
                  </div>
                  <p className="text-white font-semibold text-sm">{current.name}</p>
                  <p className="text-gray-500 text-xs">{current.role}</p>
                  <p className="font-mono text-[10px] text-accent-crimson uppercase tracking-wider mt-0.5">
                    {current.items}
                  </p>
                </div>
              </div>
            </blockquote>
          </div>

          {/* Navigation dots */}
          <div className="flex items-center gap-3 mt-12">
            {testimonials.map((t, i) =>
            <button
              key={t.id}
              onClick={() => handleSelect(i)}
              aria-label={`View testimonial from ${t.name}`}
              className={`rounded-full transition-all duration-500 ${
              i === active ?
              "w-8 h-2 bg-accent-crimson" : "w-2 h-2 bg-white/20 hover:bg-white/40"}`
              } />

            )}
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => handleSelect((active - 1 + testimonials.length) % testimonials.length)}
                className="w-9 h-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-gray-500 hover:text-white transition-all"
                aria-label="Previous testimonial">
                
                <Icon name="ArrowLeftIcon" size={14} />
              </button>
              <button
                onClick={() => handleSelect((active + 1) % testimonials.length)}
                className="w-9 h-9 rounded-full border border-white/10 hover:border-white/30 flex items-center justify-center text-gray-500 hover:text-white transition-all"
                aria-label="Next testimonial">
                
                <Icon name="ArrowRightIcon" size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

}